import { injectable } from 'inversify';
import {
  constraint_item_types,
  constraint_modifier_condition_full,
  constraint_modifier_full,
  type constraint_modifier_repeater_full,
  constraint_scopes,
} from '@Api';
import {
  ConstraintItemType,
  ConstraintModifier,
  ConstraintModifierAction,
  ConstraintModifierCondition,
  ConstraintModifierConditionField,
  ConstraintModifierConditionType,
  ConstraintModifierField,
  ConstraintModifierRepeater,
  ConstraintModifierRepeaterField,
  ConstraintScope,
} from '@Models';

@injectable()
export class ConstraintModifierMapper {
  public map(item: constraint_modifier_full): ConstraintModifier {
    return {
      name: item.name,
      action: this.mapAction(item.action),
      field: this.mapField(item.field),
      value: item.value,
      childId: item.child_id,
      childType: item.child_type ? this.mapChildType(item.child_type) : null,
      conditions:
        item.conditions?.map((condition) => this.mapCondition(condition)) || [],
      repeats: item.repeats?.map((repeat) => this.mapRepeat(repeat)) || [],
    } satisfies ConstraintModifier as ConstraintModifier;
  }

  mapAction(action: constraint_modifier_full.action): ConstraintModifierAction {
    switch (action) {
      case constraint_modifier_full.action.INCREMENT:
        return ConstraintModifierAction.Increment;
      case constraint_modifier_full.action.APPEND:
        return ConstraintModifierAction.Append;
      case constraint_modifier_full.action.DECREMENT:
        return ConstraintModifierAction.Decrement;
    }

    return ConstraintModifierAction.Set;
  }

  mapField(field: constraint_modifier_full.field): ConstraintModifierField {
    switch (field) {
      case constraint_modifier_full.field.POINT:
        return ConstraintModifierField.Point;
      case constraint_modifier_full.field.HIDDEN:
        return ConstraintModifierField.Hidden;
      case constraint_modifier_full.field.CONSTRAINT:
        return ConstraintModifierField.Constraint;
    }

    return ConstraintModifierField.Name;
  }

  mapCondition(
    condition: constraint_modifier_condition_full,
  ): ConstraintModifierCondition {
    return {
      name: condition.name,
      field: this.mapConditionField(condition.field),
      scope: this.mapScope(condition.scope),
      value: condition.value,
      percentValue: condition.percent_value,
      shared: condition.shared,
      includeChildSelections: condition.include_child_selections,
      includeChildForces: condition.include_child_forces,
      childId: condition.child_id,
      childType: condition.child_type
        ? this.mapChildType(condition.child_type)
        : null,
      type: this.mapConditionType(condition.limit),
    } satisfies ConstraintModifierCondition as ConstraintModifierCondition;
  }

  mapConditionField(
    action: constraint_modifier_condition_full.field,
  ): ConstraintModifierConditionField {
    switch (action) {
      case constraint_modifier_condition_full.field.POINT:
        return ConstraintModifierConditionField.Point;
    }
    return ConstraintModifierConditionField.Selections;
  }

  mapScope(scope: constraint_scopes): ConstraintScope {
    switch (scope) {
      case constraint_scopes.ROSTER:
        return ConstraintScope.Roster;
      case constraint_scopes.FORCE:
        return ConstraintScope.Force;
    }

    return ConstraintScope.Parent;
  }

  mapConditionType(
    type: constraint_modifier_condition_full.limit,
  ): ConstraintModifierConditionType {
    switch (type) {
      case constraint_modifier_condition_full.limit.EQUAL_TO:
        return ConstraintModifierConditionType.EqualTo;
      case constraint_modifier_condition_full.limit.AT_MOST:
        return ConstraintModifierConditionType.AtMost;
    }
    return ConstraintModifierConditionType.AtLeast;
  }

  mapRepeat(
    repeat: constraint_modifier_repeater_full,
  ): ConstraintModifierRepeater {
    return {
      name: repeat.name,
      field: this.mapRepeatField(repeat.field),
      scope: this.mapScope(repeat.scope),
      value: repeat.value,
      percentValue: repeat.percent_value,
      shared: repeat.shared,
      includeChildSelections: repeat.include_child_selections,
      includeChildForces: repeat.include_child_forces,
      childId: repeat.child_id,
      childType: repeat.child_type
        ? this.mapChildType(repeat.child_type)
        : null,
      repeats: repeat.repeats,
      roundUp: repeat.round_up,
    } satisfies ConstraintModifierRepeater as ConstraintModifierRepeater;
  }

  mapRepeatField(
    field: constraint_modifier_repeater_full.field,
  ): ConstraintModifierRepeaterField {
    return ConstraintModifierRepeaterField.Selections;
  }

  mapChildType(type: constraint_item_types): ConstraintItemType {
    switch (type) {
      case constraint_item_types.NINTH_AGE_UNIT_OPTION:
        return ConstraintItemType.UnitOption;
      case constraint_item_types.NINTH_AGE_TROOP:
        return ConstraintItemType.Troop;
      case constraint_item_types.NINTH_AGE_ARMY_ORGANISATION:
        return ConstraintItemType.ArmyOrganisation;
      case constraint_item_types.NINTH_AGE_ARMY:
        return ConstraintItemType.Army;
      case constraint_item_types.NINTH_AGE_CONSTRAINT:
        return ConstraintItemType.Constraint;
    }

    return ConstraintItemType.Unit;
  }
}
